// src/components/Login.js
import React, { useState } from "react";
import { auth, googleProvider } from "../firebase";
import {
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";

const Login = ({ isDarkMode }) => {
  const [mode, setMode] = useState("login"); // "login" | "signup"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const d = isDarkMode;

  const styles = {
    wrapper: {
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      background: d ? "#09090b" : "#f4f4f5",
      fontFamily: "'Inter', 'Segoe UI', sans-serif",
      padding: "1rem",
      transition: "background 0.3s",
    },
    card: {
      width: "100%",
      maxWidth: "400px",
      background: d ? "rgba(24,24,27,0.95)" : "rgba(255,255,255,0.95)",
      border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
      borderRadius: "20px",
      padding: "2.5rem 2rem",
      boxShadow: d
        ? "0 25px 50px rgba(0,0,0,0.5)"
        : "0 25px 50px rgba(0,0,0,0.08)",
    },
    logo: {
      textAlign: "center",
      marginBottom: "2rem",
    },
    logoIcon: {
      fontSize: "2.5rem",
      display: "block",
      marginBottom: "0.5rem",
    },
    logoTitle: {
      fontSize: "1.375rem",
      fontWeight: "700",
      color: d ? "#fafafa" : "#09090b",
      letterSpacing: "-0.02em",
      margin: 0,
    },
    logoSub: {
      fontSize: "0.8rem",
      color: d ? "#71717a" : "#a1a1aa",
      marginTop: "0.25rem",
    },
    tabRow: {
      display: "flex",
      background: d ? "#18181b" : "#f4f4f5",
      borderRadius: "12px",
      padding: "4px",
      marginBottom: "1.75rem",
      border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
    },
    tab: (active) => ({
      flex: 1,
      padding: "0.6rem",
      border: "none",
      borderRadius: "9px",
      cursor: "pointer",
      fontSize: "0.85rem",
      fontWeight: "600",
      transition: "all 0.2s",
      background: active ? (d ? "#27272a" : "#fff") : "transparent",
      color: active ? (d ? "#fafafa" : "#09090b") : d ? "#71717a" : "#a1a1aa",
      boxShadow: active
        ? d
          ? "0 1px 3px rgba(0,0,0,0.4)"
          : "0 1px 3px rgba(0,0,0,0.08)"
        : "none",
    }),
    inputGroup: { marginBottom: "1rem" },
    label: {
      display: "block",
      fontSize: "0.78rem",
      fontWeight: "600",
      color: d ? "#a1a1aa" : "#71717a",
      marginBottom: "0.4rem",
      textTransform: "uppercase",
      letterSpacing: "0.05em",
    },
    input: {
      width: "100%",
      padding: "0.8rem 1rem",
      background: d ? "#09090b" : "#f4f4f5",
      border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
      borderRadius: "12px",
      color: d ? "#fafafa" : "#09090b",
      fontSize: "0.9rem",
      outline: "none",
      boxSizing: "border-box",
      transition: "border-color 0.2s",
      fontFamily: "inherit",
    },
    btn: {
      width: "100%",
      padding: "0.875rem",
      borderRadius: "12px",
      border: "none",
      cursor: "pointer",
      fontSize: "0.9rem",
      fontWeight: "600",
      transition: "all 0.2s",
      fontFamily: "inherit",
    },
    btnPrimary: {
      background: "linear-gradient(135deg, #10b981, #059669)",
      color: "#fff",
      marginTop: "0.5rem",
      boxShadow: "0 4px 15px rgba(16,185,129,0.3)",
    },
    divider: {
      display: "flex",
      alignItems: "center",
      gap: "0.75rem",
      margin: "1.25rem 0",
    },
    dividerLine: {
      flex: 1,
      height: "1px",
      background: d ? "#27272a" : "#e4e4e7",
    },
    dividerText: {
      fontSize: "0.75rem",
      color: d ? "#52525b" : "#a1a1aa",
      fontWeight: "500",
    },
    btnGoogle: {
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "0.6rem",
      background: d ? "#18181b" : "#fff",
      border: `1px solid ${d ? "#27272a" : "#e4e4e7"}`,
      color: d ? "#fafafa" : "#09090b",
      width: "100%",
      padding: "0.8rem",
      borderRadius: "12px",
      cursor: "pointer",
      fontSize: "0.875rem",
      fontWeight: "600",
      transition: "all 0.2s",
      fontFamily: "inherit",
      boxShadow: d ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
    },
    error: {
      background: "rgba(239,68,68,0.08)",
      border: "1px solid rgba(239,68,68,0.2)",
      color: "#f87171",
      borderRadius: "10px",
      padding: "0.7rem 1rem",
      fontSize: "0.8rem",
      fontWeight: "500",
      marginTop: "0.75rem",
      textAlign: "center",
    },
  };

  const getErrorMsg = (code) => {
    const map = {
      "auth/user-not-found": "ไม่พบบัญชีผู้ใช้นี้",
      "auth/wrong-password": "รหัสผ่านไม่ถูกต้อง",
      "auth/email-already-in-use": "อีเมลนี้ถูกใช้งานแล้ว",
      "auth/weak-password": "รหัสผ่านต้องมีอย่างน้อย 6 ตัวอักษร",
      "auth/invalid-email": "รูปแบบอีเมลไม่ถูกต้อง",
      "auth/invalid-credential": "อีเมลหรือรหัสผ่านไม่ถูกต้อง",
      "auth/popup-closed-by-user": "ปิด popup ก่อนที่จะ login เสร็จ",
    };
    return map[code] || "เกิดข้อผิดพลาด กรุณาลองใหม่";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (mode === "signup" && password !== confirmPassword) {
      return setError("รหัสผ่านไม่ตรงกัน");
    }
    setLoading(true);
    try {
      if (mode === "login") {
        await signInWithEmailAndPassword(auth, email, password);
      } else {
        await createUserWithEmailAndPassword(auth, email, password);
      }
    } catch (err) {
      setError(getErrorMsg(err.code));
      setLoading(false);
    }
  };

  const handleGoogle = async () => {
    setError("");
    setLoading(true);
    try {
      await signInWithPopup(auth, googleProvider);
    } catch (err) {
      setError(getErrorMsg(err.code));
      setLoading(false);
    }
  };

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        {/* Logo */}
        <div style={styles.logo}>
          <span style={styles.logoIcon}>🇯🇵</span>
          <h1 style={styles.logoTitle}>Japan Planner</h1>
          <p style={styles.logoSub}>Plan your perfect Japan trip</p>
        </div>

        {/* Tabs */}
        <div style={styles.tabRow}>
          <button
            style={styles.tab(mode === "login")}
            onClick={() => {
              setMode("login");
              setError("");
            }}
          >
            Login
          </button>
          <button
            style={styles.tab(mode === "signup")}
            onClick={() => {
              setMode("signup");
              setError("");
            }}
          >
            Sign Up
          </button>
        </div>

        {/* Google Button */}
        <button
          style={styles.btnGoogle}
          onClick={handleGoogle}
          disabled={loading}
          onMouseEnter={(e) =>
            (e.currentTarget.style.background = d ? "#27272a" : "#f4f4f5")
          }
          onMouseLeave={(e) =>
            (e.currentTarget.style.background = d ? "#18181b" : "#fff")
          }
        >
          <svg width="18" height="18" viewBox="0 0 48 48">
            <path
              fill="#EA4335"
              d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
            />
            <path
              fill="#4285F4"
              d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
            />
            <path
              fill="#FBBC05"
              d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
            />
            <path
              fill="#34A853"
              d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.97 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
            />
          </svg>
          Continue with Google
        </button>

        {/* Divider */}
        <div style={styles.divider}>
          <div style={styles.dividerLine} />
          <span style={styles.dividerText}>or</span>
          <div style={styles.dividerLine} />
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit}>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Email</label>
            <input
              style={styles.input}
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              onFocus={(e) => (e.target.style.borderColor = "#10b981")}
              onBlur={(e) =>
                (e.target.style.borderColor = d ? "#27272a" : "#e4e4e7")
              }
            />
          </div>
          <div style={styles.inputGroup}>
            <label style={styles.label}>Password</label>
            <input
              style={styles.input}
              type="password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              onFocus={(e) => (e.target.style.borderColor = "#10b981")}
              onBlur={(e) =>
                (e.target.style.borderColor = d ? "#27272a" : "#e4e4e7")
              }
            />
          </div>
          {mode === "signup" && (
            <div style={styles.inputGroup}>
              <label style={styles.label}>Confirm Password</label>
              <input
                style={styles.input}
                type="password"
                placeholder="••••••••"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                onFocus={(e) => (e.target.style.borderColor = "#10b981")}
                onBlur={(e) =>
                  (e.target.style.borderColor = d ? "#27272a" : "#e4e4e7")
                }
              />
            </div>
          )}
          {error && <div style={styles.error}>{error}</div>}
          <button
            type="submit"
            style={{
              ...styles.btn,
              ...styles.btnPrimary,
              opacity: loading ? 0.7 : 1,
            }}
            disabled={loading}
            onMouseEnter={(e) =>
              !loading && (e.currentTarget.style.transform = "translateY(-1px)")
            }
            onMouseLeave={(e) =>
              (e.currentTarget.style.transform = "translateY(0)")
            }
          >
            {loading
              ? "กำลังดำเนินการ..."
              : mode === "login"
              ? "Login"
              : "Create Account"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default Login;
